import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Eye, Link2, Copy, Pencil, Trash2, 
  BarChart3, Clock, CheckCircle, Calendar, 
  Users, MousePointer, TrendingUp, ExternalLink,
  Phone, Mail, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export const BusinessLandingsSection = ({ user }) => {
  const [landings, setLandings] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('landings'); // 'landings', 'appointments', 'analytics'
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    offer_type: 'discount',
    offer_value: '',
    offer_original_price: '',
    validity_days: 7,
    cta_type: 'book_appointment',
    cta_text: 'Reservar Ahora',
    image_url: '',
    theme_color: '#4A7C59'
  });

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [user?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'X-Business-Id': user.id };
      
      const [landingsRes, appointmentsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/api/landings/business`, { headers }),
        axios.get(`${API}/api/landings/business/appointments`, { headers }),
        axios.get(`${API}/api/landings/business/analytics?days=30`, { headers })
      ]);
      
      setLandings(landingsRes.data.landings || []);
      setAppointments(appointmentsRes.data.appointments || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createLanding = async (e) => {
    e.preventDefault();
    
    try {
      const res = await axios.post(`${API}/api/landings/business`, {
        landing_config: {
          ...formData,
          offer_value: formData.offer_value ? parseFloat(formData.offer_value) : null,
          offer_original_price: formData.offer_original_price ? parseFloat(formData.offer_original_price) : null
        }
      }, {
        headers: { 'X-Business-Id': user.id }
      });
      
      toast.success('Landing creada correctamente');
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        offer_type: 'discount',
        offer_value: '',
        offer_original_price: '',
        validity_days: 7,
        cta_type: 'book_appointment',
        cta_text: 'Reservar Ahora',
        image_url: '',
        theme_color: '#4A7C59'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear landing');
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/oferta/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Enlace copiado');
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.patch(
        `${API}/api/landings/business/appointments/${appointmentId}?status=${status}`,
        {},
        { headers: { 'X-Business-Id': user.id } }
      );
      toast.success(`Cita ${status === 'confirmed' ? 'confirmada' : status === 'completed' ? 'completada' : 'actualizada'}`);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar cita');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-local-sage" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-manrope font-bold text-2xl flex items-center gap-2">
            <Link2 className="w-7 h-7 text-local-sage" />
            Landing Pages & Citas
          </h2>
          <p className="text-muted-foreground mt-1">
            Crea páginas de oferta y gestiona reservas sin necesidad de cupones
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-local-sage hover:bg-local-sage/90">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Landing
        </Button>
      </div>

      {/* Stats Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Eye className="w-4 h-4" />
              <span className="text-sm">Visitas</span>
            </div>
            <p className="text-2xl font-bold">{analytics.total_views || 0}</p>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <MousePointer className="w-4 h-4" />
              <span className="text-sm">Clics CTA</span>
            </div>
            <p className="text-2xl font-bold">{analytics.total_cta_clicks || 0}</p>
            <p className="text-xs text-emerald-600">{analytics.cta_rate || 0}% ratio</p>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Conversiones</span>
            </div>
            <p className="text-2xl font-bold">{analytics.total_conversions || 0}</p>
            <p className="text-xs text-emerald-600">{analytics.conversion_rate || 0}% ratio</p>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 mb-2">
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm">Valor Total</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{formatCurrency(analytics.total_value)}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        {[
          { id: 'landings', label: 'Mis Landings', count: landings.length },
          { id: 'appointments', label: 'Citas', count: appointments.filter(a => a.status === 'pending' || a.status === 'confirmed').length },
          { id: 'analytics', label: 'Analíticas' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id 
                ? 'border-local-sage text-local-sage' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Landings Tab */}
      {activeTab === 'landings' && (
        <div className="space-y-4">
          {landings.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Link2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No tienes landing pages creadas</p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crear mi primera landing
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {landings.map(landing => (
                <motion.div
                  key={landing.landing_id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-card rounded-xl border border-border overflow-hidden"
                >
                  {landing.image_url && (
                    <div className="h-32 bg-gray-100">
                      <img src={landing.image_url} alt={landing.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{landing.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{landing.description}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        landing.status === 'active' 
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {landing.status === 'active' ? 'Activa' : 'Pausada'}
                      </span>
                    </div>
                    
                    {/* Metrics */}
                    <div className="flex gap-4 mt-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {landing.metrics?.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MousePointer className="w-4 h-4" />
                        {landing.metrics?.cta_clicks || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {landing.metrics?.conversions || 0}
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyLink(landing.slug)}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copiar enlace
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/oferta/${landing.slug}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Appointments Tab */}
      {activeTab === 'appointments' && (
        <div className="space-y-4">
          {appointments.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No tienes citas pendientes</p>
            </div>
          ) : (
            <div className="space-y-3">
              {appointments.map(apt => (
                <div
                  key={apt.appointment_id}
                  className="bg-card rounded-xl border border-border p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-local-sage/10 flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-local-sage" />
                      </div>
                      <div>
                        <p className="font-semibold">{apt.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{apt.offer_title}</p>
                        <div className="flex gap-3 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(apt.datetime).toLocaleDateString('es-ES')} - {new Date(apt.datetime).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {apt.customer_phone && (
                            <a href={`tel:${apt.customer_phone}`} className="flex items-center gap-1 hover:text-local-sage">
                              <Phone className="w-4 h-4" />
                              {apt.customer_phone}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        apt.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        apt.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                        apt.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {apt.status === 'pending' ? 'Pendiente' :
                         apt.status === 'confirmed' ? 'Confirmada' :
                         apt.status === 'completed' ? 'Completada' :
                         apt.status === 'cancelled' ? 'Cancelada' : apt.status}
                      </span>
                    </div>
                  </div>
                  
                  {(apt.status === 'pending' || apt.status === 'confirmed') && (
                    <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                      {apt.status === 'pending' && (
                        <Button size="sm" onClick={() => updateAppointmentStatus(apt.appointment_id, 'confirmed')}>
                          Confirmar
                        </Button>
                      )}
                      {apt.status === 'confirmed' && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateAppointmentStatus(apt.appointment_id, 'completed')}>
                          Marcar completada
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => updateAppointmentStatus(apt.appointment_id, 'cancelled')}>
                        Cancelar
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-semibold mb-4">Rendimiento de tus Landings (últimos 30 días)</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-3xl font-bold">{analytics.total_views || 0}</p>
              <p className="text-sm text-muted-foreground">Visitas totales</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{analytics.total_cta_clicks || 0}</p>
              <p className="text-sm text-muted-foreground">Clics en CTA</p>
              <p className="text-sm text-emerald-600">{analytics.cta_rate || 0}% de las visitas</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{analytics.total_conversions || 0}</p>
              <p className="text-sm text-muted-foreground">Conversiones</p>
              <p className="text-sm text-emerald-600">{analytics.conversion_rate || 0}% ratio</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-emerald-600">{formatCurrency(analytics.total_value)}</p>
              <p className="text-sm text-muted-foreground">Valor generado</p>
            </div>
          </div>

          {/* Per Landing Breakdown */}
          {Object.keys(analytics.by_landing || {}).length > 0 && (
            <div className="mt-6 pt-6 border-t border-border">
              <h4 className="font-medium mb-4">Desglose por Landing</h4>
              <div className="space-y-2">
                {Object.entries(analytics.by_landing).map(([lid, stats]) => {
                  const landing = landings.find(l => l.landing_id === lid);
                  return (
                    <div key={lid} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <span className="font-medium">{landing?.title || lid}</span>
                      <div className="flex gap-6 text-sm">
                        <span>{stats.views} visitas</span>
                        <span>{stats.conversions} conv.</span>
                        <span className="text-emerald-600">{formatCurrency(stats.value)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Landing Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Nueva Landing Page</h3>
                <button onClick={() => setShowCreateModal(false)}>
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            <form onSubmit={createLanding} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Título de la oferta *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Ej: 20% de descuento en tu primera cita"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
                  rows={3}
                  placeholder="Describe tu oferta en detalle..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo de oferta</label>
                  <select
                    value={formData.offer_type}
                    onChange={(e) => setFormData({ ...formData, offer_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                  >
                    <option value="discount">Descuento %</option>
                    <option value="service">Servicio</option>
                    <option value="product">Producto</option>
                    <option value="appointment">Solo cita</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Valor/Precio (€)</label>
                  <Input
                    type="number"
                    value={formData.offer_value}
                    onChange={(e) => setFormData({ ...formData, offer_value: e.target.value })}
                    placeholder={formData.offer_type === 'discount' ? '20' : '50'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Acción del botón</label>
                  <select
                    value={formData.cta_type}
                    onChange={(e) => setFormData({ ...formData, cta_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                  >
                    <option value="book_appointment">Reservar cita</option>
                    <option value="contact">Contactar</option>
                    <option value="reserve">Reservar</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Texto del botón</label>
                  <Input
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    placeholder="Reservar Ahora"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Días de validez</label>
                <Input
                  type="number"
                  value={formData.validity_days}
                  onChange={(e) => setFormData({ ...formData, validity_days: parseInt(e.target.value) || 7 })}
                  min={1}
                  max={90}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">URL de imagen (opcional)</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-local-sage hover:bg-local-sage/90">
                  Crear Landing
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};
